import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, DialogActions, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { Stack } from '@mui/system';
import { useState } from 'react';

import { useBasicExitIntent } from '@/hooks/useBasicExitIntent';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

const Popup1120pol = () => {
  const [open, setOpen] = useState(false);

  useBasicExitIntent(() => {
    setOpen(true);
  });

  const handleClose = () => {
    sessionStorage.setItem('tzo-exit-intent', 'exit');
    setOpen(false);
  };

  return (
    <>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <BootstrapDialogTitle
          id="customized-dialog-title"
          onClose={handleClose}
        />
        <DialogContent>
          <Box component="div" sx={{ textAlign: 'center', mt: 0 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 2, md: 3 }}
              alignItems="center"
              justifyContent="space-around"
              sx={{ px: { xs: 0, sm: 2 } }}
            >
              <Box sx={{ textAlign: { xs: 'center', sm: 'center' } }}>
                <Typography
                  variant="h2"
                  color="primary"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: 22, sm: 28, md: 32 },
                    mb: 0,
                  }}
                >
                  {' '}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: 22, sm: 28, md: 30 },
                      color: '#da4b4b',
                    }}
                  >
                    📣 Attention!
                    <span style={{ color: '#0d0b0b' }}>
                      {' '}
                      Before You Go...
                    </span>{' '}
                    📣
                  </Box>{' '}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: 18, sm: 18, md: 22 },
                    my: 0,
                    textAlign: 'center',
                    maxWidth: 580,
                    mx: 'auto',
                    mb: 1,
                    mt: 2,
                  }}
                >
                  File your{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    2025 Form 1120-POL
                  </strong>{' '}
                  with Us &{' '}
                  <strong style={{ color: '#1da94a' }}> Save 10%</strong>{' '}
                </Typography>
              </Box>
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'column' }}
              spacing={{ xs: 1, sm: 2, md: 4 }}
              sx={{ borderTop: '1px solid #ccc', pt: 0 }}
            >
              <Box>
                <Typography
                  component="div"
                  variant="body1"
                  sx={{
                    fontSize: { xs: 18, sm: 18, md: 18 },
                    my: 0,
                    textAlign: 'center',
                    maxWidth: 580,
                    mx: 'auto',
                  }}
                >
                  <Box
                    component="ul"
                    sx={{ listStyleType: 'none', pl: { xs: 0, sm: 3 } }}
                  >
                    <Box
                      component="li"
                      sx={{
                        display: { xs: 'inline-block', sm: 'flex' },
                        alignItems: 'center',
                        mb: 1.25,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{
                          mr: 1,
                          color: '#1da94a',
                          mb: { xs: -0.6, sm: 0 },
                        }}
                      />
                      Simple online filing for Section 527 political
                      organizations.
                    </Box>
                    <Box
                      component="li"
                      sx={{
                        display: { xs: 'inline-block', sm: 'flex' },
                        alignItems: 'center',
                        mb: 1.25,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{
                          mr: 1,
                          color: '#1da94a',
                          mb: { xs: -0.6, sm: 0 },
                        }}
                      />
                      IRS-authorized e-filing with built-in error checks.
                    </Box>
                    <Box
                      component="li"
                      sx={{
                        display: { xs: 'inline-block', sm: 'flex' },
                        alignItems: 'center',
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{
                          mr: 1,
                          color: '#1da94a',
                          mb: { xs: -0.6, sm: 0 },
                        }}
                      />
                      Supports&nbsp;<b>1120-POL</b>&nbsp;extension filings.
                    </Box>
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: 16, sm: 18, md: 18 },
                    my: 0,
                  }}
                >
                  Use code{' '}
                  <Box
                    component="strong"
                    sx={{
                      ml: 0,
                      color: '#c02652',
                      fontWeight: 600,
                      border: '2px dashed rgb(113, 81, 0)',
                      px: 1,
                      backgroundColor: 'rgb(255, 250, 205)',
                    }}
                  >
                    TZOTE10
                  </Box>{' '}
                  at checkout to{' '}
                  <span style={{ fontWeight: 600 }}> save 10%</span> on your
                  filing fee!
                </Typography>
              </Box>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            py: '24px !important',
            textAlign: 'center',
            backgroundColor: '#e3e4e8',
          }}
        >
          <Box sx={{ px: { xs: 1, sm: 3 } }}>
            <Button
              variant="contained"
              size="large"
              className="btn-primary"
              sx={{ textTransform: 'none', display: 'inline-block' }}
              href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=exitpopup`}
            >
              E-file Now
            </Button>
          </Box>
        </DialogActions>
      </BootstrapDialog>
    </>
  );
};

export default Popup1120pol;
